import { AnchorTitle } from "#components/ui/anchor-title"
import { Callout } from "#components/ui/callout"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "#components/ui/card"
import { Separator } from "#components/ui/separator"
import { StepItem, StepList } from "#components/ui/step-list"
import { Body, H3 } from "#components/ui/typography"
import { useI18n } from "#hooks/use-i18n"
import { Filter, Pencil, Plus, Trash2 } from "lucide-react"

export function ExpensesHelp() {
  const { t } = useI18n()

  return (
    <div className="space-y-6">
      <div>
        <H3 className="mb-2">{t("pages.help.expenses.overview.title")}</H3>
        <Body className="text-muted-foreground">
          {t("pages.help.expenses.overview.description")}
        </Body>
      </div>

      <Card id="expenses-table">
        <CardHeader>
          <CardTitle>
            <AnchorTitle id="expenses-table">{t("pages.help.expenses.table.title")}</AnchorTitle>
          </CardTitle>
          <CardDescription>{t("pages.help.expenses.table.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Body className="font-semibold mb-2">
              {t("pages.help.expenses.table.columns.title")}
            </Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.expenses.table.columns.description")}
            </Body>
          </div>

          <Separator />

          <div>
            <Body className="font-semibold mb-2">
              {t("pages.help.expenses.table.sorting.title")}
            </Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.expenses.table.sorting.description")}
            </Body>
          </div>

          <Separator />

          <div>
            <Body className="font-semibold mb-2">
              {t("pages.help.expenses.table.pagination.title")}
            </Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.expenses.table.pagination.description")}
            </Body>
          </div>
        </CardContent>
      </Card>

      <Card id="expenses-actions">
        <CardHeader>
          <CardTitle>
            <AnchorTitle id="expenses-actions">
              {t("pages.help.expenses.actions.title")}
            </AnchorTitle>
          </CardTitle>
          <CardDescription>{t("pages.help.expenses.actions.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-start gap-3">
            <Plus className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
            <div className="flex-1">
              <Body className="font-semibold mb-1">
                {t("pages.help.expenses.actions.add.title")}
              </Body>
              <Body className="text-muted-foreground text-sm">
                {t("pages.help.expenses.actions.add.description")}
              </Body>
            </div>
          </div>

          <Separator />

          <div className="flex items-start gap-3">
            <Pencil className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
            <div className="flex-1">
              <Body className="font-semibold mb-1">
                {t("pages.help.expenses.actions.edit.title")}
              </Body>
              <Body className="text-muted-foreground text-sm">
                {t("pages.help.expenses.actions.edit.description")}
              </Body>
            </div>
          </div>

          <Separator />

          <div className="flex items-start gap-3">
            <Trash2 className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
            <div className="flex-1">
              <Body className="font-semibold mb-1">
                {t("pages.help.expenses.actions.delete.title")}
              </Body>
              <Body className="text-muted-foreground text-sm">
                {t("pages.help.expenses.actions.delete.description")}
              </Body>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card id="expenses-form">
        <CardHeader>
          <CardTitle>
            <AnchorTitle id="expenses-form">{t("pages.help.expenses.form.title")}</AnchorTitle>
          </CardTitle>
          <CardDescription>{t("pages.help.expenses.form.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Body className="font-semibold mb-2">
              {t("pages.help.expenses.form.basicFields.title")}
            </Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.expenses.form.basicFields.description")}
            </Body>
          </div>

          <Separator />

          <div>
            <Body className="font-semibold mb-2">
              {t("pages.help.expenses.form.recurring.title")}
            </Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.expenses.form.recurring.description")}
            </Body>
          </div>

          <Separator />

          <div>
            <Body className="font-semibold mb-2">{t("pages.help.expenses.form.splits.title")}</Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.expenses.form.splits.description")}
            </Body>
          </div>
        </CardContent>
      </Card>

      <Card id="expenses-filters">
        <CardHeader>
          <CardTitle>
            <AnchorTitle id="expenses-filters">
              {t("pages.help.expenses.filters.title")}
            </AnchorTitle>
          </CardTitle>
          <CardDescription>{t("pages.help.expenses.filters.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-start gap-3">
            <Filter className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
            <div className="flex-1">
              <Body className="font-semibold mb-1">
                {t("pages.help.expenses.filters.search.title")}
              </Body>
              <Body className="text-muted-foreground text-sm">
                {t("pages.help.expenses.filters.search.description")}
              </Body>
            </div>
          </div>

          <Separator />

          <div>
            <Body className="font-semibold mb-2">
              {t("pages.help.expenses.filters.category.title")}
            </Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.expenses.filters.category.description")}
            </Body>
          </div>

          <Separator />

          <div>
            <Body className="font-semibold mb-2">
              {t("pages.help.expenses.filters.recurring.title")}
            </Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.expenses.filters.recurring.description")}
            </Body>
          </div>

          <Separator />

          <div>
            <Body className="font-semibold mb-2">
              {t("pages.help.expenses.filters.virtual.title")}
            </Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.expenses.filters.virtual.description")}
            </Body>
          </div>
        </CardContent>
      </Card>

      <Card id="expenses-csv-import">
        <CardHeader>
          <CardTitle>
            <AnchorTitle id="expenses-csv-import">
              {t("pages.help.expenses.csvImport.title")}
            </AnchorTitle>
          </CardTitle>
          <CardDescription>{t("pages.help.expenses.csvImport.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Callout variant="info">
            <p>
              <strong>Quick Start:</strong> The CSV import process guides you through each step. You
              can pause, review, and edit at any time before finalizing.
            </p>
          </Callout>

          <StepList>
            <StepItem step={1}>
              <div>
                <Body className="font-semibold mb-1">
                  {t("pages.help.expenses.csvImport.step1.title")}
                </Body>
                <Body className="text-muted-foreground text-sm">
                  {t("pages.help.expenses.csvImport.step1.description")}
                </Body>
              </div>
            </StepItem>

            <StepItem step={2}>
              <div>
                <Body className="font-semibold mb-1">
                  {t("pages.help.expenses.csvImport.step2.title")}
                </Body>
                <Body className="text-muted-foreground text-sm">
                  {t("pages.help.expenses.csvImport.step2.description")}
                </Body>
              </div>
            </StepItem>

            <StepItem step={3}>
              <div>
                <Body className="font-semibold mb-1">
                  {t("pages.help.expenses.csvImport.step3.title")}
                </Body>
                <Body className="text-muted-foreground text-sm">
                  {t("pages.help.expenses.csvImport.step3.description")}
                </Body>
              </div>
            </StepItem>

            <StepItem step={4}>
              <div>
                <Body className="font-semibold mb-1">
                  {t("pages.help.expenses.csvImport.step4.title")}
                </Body>
                <Body className="text-muted-foreground text-sm">
                  {t("pages.help.expenses.csvImport.step4.description")}
                </Body>
              </div>
            </StepItem>

            <StepItem step={5}>
              <div>
                <Body className="font-semibold mb-1">
                  {t("pages.help.expenses.csvImport.step5.title")}
                </Body>
                <Body className="text-muted-foreground text-sm">
                  {t("pages.help.expenses.csvImport.step5.description")}
                </Body>
              </div>
            </StepItem>
          </StepList>

          <Separator />

          <div>
            <Body className="font-semibold mb-2">
              {t("pages.help.expenses.csvImport.progress.title")}
            </Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.expenses.csvImport.progress.description")}
            </Body>
          </div>
        </CardContent>
      </Card>

      <Card id="expenses-permissions">
        <CardHeader>
          <CardTitle>
            <AnchorTitle id="expenses-permissions">
              {t("pages.help.expenses.permissions.title")}
            </AnchorTitle>
          </CardTitle>
          <CardDescription>{t("pages.help.expenses.permissions.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Body className="text-muted-foreground text-sm">
            {t("pages.help.expenses.permissions.content")}
          </Body>
        </CardContent>
      </Card>
    </div>
  )
}
